import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowDown, ArrowRight, ArrowUp, FileText, Gift, MapPin, Package, Sparkles, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function FeaturedSectionFormModal({
  open,
  onOpenChange,
  editingFeatured,
  featuredFormData,
  setFeaturedFormData,
  browseSections,
  selectedItems,
  selectedItemTypes,
  showItemSelector,
  setShowItemSelector,
  searchQuery,
  setSearchQuery,
  products,
  decks,
  handleItemToggle,
  handleCreateFeaturedSection,
  getItemName
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto admin-area">
        <DialogHeader className="pb-4 border-b" style={{ borderColor: '#E5E5E5' }}>
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2D4354 0%, #534145 100%)' }}
            >
              <Sparkles className="w-6 h-6" style={{ color: '#FED7A5' }} />
            </div>
            <div>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif', color: '#20212B', fontSize: '24px' }}>
                {editingFeatured ? 'Edit Featured Section' : 'Create Featured Section'}
              </DialogTitle>
              <p className="text-sm mt-1" style={{ color: '#73766A' }}>
                Curate a highlighted collection that stands out on the browse page
              </p>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-6 pt-6">
          {/* Basic Information Section */}
          <div 
            className="p-6 rounded-xl space-y-4" 
            style={{ background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFBF7 100%)', border: '1px solid #FED7A5' }}
          >
            <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}>
              <FileText className="w-5 h-5" style={{ color: '#9E6752' }} />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#20212B' }}>
                  Section Name *
                </label>
                <Input
                  placeholder="e.g., Editor's Picks, Holiday Favorites"
                  value={featuredFormData.name}
                  onChange={(e) => setFeaturedFormData({ ...featuredFormData, name: e.target.value })}
                  required
                  className="border-2 focus:border-[#FED7A5]"
                  style={{ fontSize: '14px' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#20212B' }}>
                  Display In Category *
                </label>
                <select
                  value={featuredFormData.category_name}
                  onChange={(e) => setFeaturedFormData({ ...featuredFormData, category_name: e.target.value })}
                  className="w-full px-3 py-2 border-2 rounded-md focus:border-[#FED7A5] focus:outline-none"
                  style={{ fontSize: '14px' }}
                  required
                >
                  <option value="">Select a category...</option>
                  {browseSections.map(section => (
                    <option key={section.name} value={section.name}>
                      {section.name}
                    </option>
                  ))}
                </select>
                {featuredFormData.category_name && (
                  <p className="text-xs mt-2 px-3 py-1 rounded-full inline-block" style={{ background: '#2D4354', color: '#FED7A5' }}>
                    ✓ {browseSections.find(s => s.name === featuredFormData.category_name)?.description}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#20212B' }}>
                Description
              </label>
              <textarea
                placeholder="Add a compelling description that entices visitors..."
                value={featuredFormData.description}
                onChange={(e) => setFeaturedFormData({ ...featuredFormData, description: e.target.value })}
                className="w-full px-3 py-2 border-2 rounded-md focus:border-[#FED7A5] focus:outline-none"
                style={{ fontSize: '14px' }}
                rows="3"
              />
            </div>
          </div>

          {/* Placement Section */}
          <div 
            className="p-6 rounded-xl" 
            style={{ background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFBF7 100%)', border: '1px solid #FED7A5' }}
          >
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4" style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}>
              <MapPin className="w-5 h-5" style={{ color: '#9E6752' }} />
              Placement
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { value: 0, label: 'Before Products', icon: ArrowUp, desc: 'Show at the very top' },
                { value: 1, label: 'Middle', icon: ArrowRight, desc: 'After product grid' },
                { value: 2, label: 'End', icon: ArrowDown, desc: 'After all products' }
              ].map((option) => (
                <div
                  key={option.value}
                  onClick={() => setFeaturedFormData({ ...featuredFormData, order: option.value })}
                  className="p-4 rounded-lg cursor-pointer transition-all hover:scale-105"
                  style={{
                    border: `2px solid ${featuredFormData.order === option.value ? '#9E6752' : '#E5E5E5'}`,
                    background: featuredFormData.order === option.value ? '#FFF8F0' : 'white'
                  }}
                >
                  <div className="text-center">
                    <option.icon className="w-6 h-6 mx-auto mb-2" style={{ color: featuredFormData.order === option.value ? '#9E6752' : '#73766A' }} />
                    <div className="font-semibold text-sm" style={{ color: '#20212B' }}>{option.label}</div>
                    <div className="text-xs mt-1" style={{ color: '#73766A' }}>{option.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Items Selection Section */}
          <div 
            className="p-6 rounded-xl space-y-4" 
            style={{ background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFBF7 100%)', border: '1px solid #FED7A5' }}
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}>
                  <Gift className="w-5 h-5" style={{ color: '#9E6752' }} />
                  Select Items
                </h3>
                <p className="text-xs mt-1" style={{ color: '#73766A' }}>
                  Choose up to 4 products or decks for this featured section
                </p>
              </div>
              <div 
                className="px-4 py-2 rounded-full font-semibold"
                style={{ 
                  background: selectedItems.length >= 4 ? '#9E6752' : '#2D4354', 
                  color: '#FED7A5' 
                }}
              >
                {selectedItems.length} / 4
              </div>
            </div>

            {selectedItems.length > 0 && (
              <div className="flex flex-wrap gap-2 p-4 rounded-lg" style={{ background: 'white', border: '1px solid #E5E5E5' }}>
                {selectedItems.map((itemId, idx) => (
                  <div
                    key={`${selectedItemTypes[idx] || 'item'}-${itemId}`}
                    className="px-4 py-2 rounded-full text-sm flex items-center gap-2 shadow-sm"
                    style={{ background: '#2D4354', color: '#FED7A5' }}
                  >
                    {selectedItemTypes[idx] === 'deck' && <Package className="w-4 h-4" />}
                    <span className="font-medium">{getItemName(itemId, selectedItemTypes[idx])}</span>
                    <button
                      type="button"
                      onClick={() => handleItemToggle(itemId, selectedItemTypes[idx])}
                      className="hover:bg-opacity-80 rounded-full p-1 transition-all"
                      style={{ background: '#534145' }}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              onClick={() => setShowItemSelector(!showItemSelector)}
              variant="outline"
              className="w-full border-2"
              style={{ borderColor: '#9E6752', color: '#9E6752' }}
            >
              {showItemSelector ? '− Hide Item Library' : '+ Browse Item Library'}
            </Button>

            {showItemSelector && (
              <div className="rounded-lg p-4 space-y-4" style={{ background: 'white', border: '2px solid #E5E5E5' }}>
                {/* Search Bar */}
                <div className="sticky top-0 z-10 pb-4" style={{ background: 'white' }}>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="🔍 Search products and decks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 pl-10 border-2 rounded-lg focus:outline-none transition-all"
                      style={{ 
                        borderColor: searchQuery ? '#9E6752' : '#E5E5E5',
                        fontSize: '14px'
                      }}
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded-full text-xs hover:bg-gray-100"
                        style={{ color: '#73766A' }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <p className="text-xs mt-2 px-2" style={{ color: '#73766A' }}>
                      Found {products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())).length} products 
                      and {decks.filter(d => d.title.toLowerCase().includes(searchQuery.toLowerCase())).length} decks
                    </p>
                  )}
                </div>

                {/* Products */}
                <div>
                  <h4 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#20212B' }}>
                    <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#FED7A5', color: '#20212B' }}>P</span>
                    Products
                  </h4>
                  <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                    {products
                      .filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map(product => {
                        const isSelected = selectedItems.includes(product.id);
                        const isDisabled = !isSelected && selectedItems.length >= 4;
                        
                        return (
                          <label 
                            key={product.id} 
                            className={`flex items-center gap-3 p-3 rounded-lg transition-all ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
                            style={{ border: isSelected ? '2px solid #9E6752' : '1px solid #E5E5E5', background: isSelected ? '#FFF8F0' : 'white' }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => !isDisabled && handleItemToggle(product.id, 'product')}
                              disabled={isDisabled}
                              className="w-4 h-4"
                              style={{ accentColor: '#9E6752' }}
                            />
                            <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded-lg shadow-sm" />
                            <div className="flex-1">
                              <span className="text-sm font-medium block" style={{ color: '#20212B' }}>{product.name}</span>
                              <span className="text-xs" style={{ color: '#73766A' }}>${product.price}</span>
                            </div>
                          </label>
                        );
                      })}
                    {products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                      <p className="text-sm p-4 rounded-lg text-center" style={{ background: '#FFF8F0', color: '#73766A' }}>
                        No products match "{searchQuery}"
                      </p>
                    )}
                  </div>
                </div>

                {/* Decks */}
                <div>
                  <h4 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#20212B' }}>
                    <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#FED7A5', color: '#20212B' }}>
                      <Package className="w-4 h-4" />
                    </span>
                    Decks (Bundles)
                  </h4>
                  {decks.length === 0 ? (
                    <p className="text-sm p-4 rounded-lg text-center" style={{ background: '#FFF8F0', color: '#73766A' }}>
                      No decks available. Create decks in the Decks tab first.
                    </p>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                      {decks
                        .filter(deck => deck.title.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map(deck => {
                          const isSelected = selectedItems.includes(deck.id);
                          const isDisabled = !isSelected && selectedItems.length >= 4;
                          
                          return (
                            <label 
                              key={deck.id} 
                              className={`flex items-center gap-3 p-3 rounded-lg transition-all ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
                              style={{ border: isSelected ? '2px solid #9E6752' : '1px solid #E5E5E5', background: isSelected ? '#FFF8F0' : 'white' }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => !isDisabled && handleItemToggle(deck.id, 'deck')}
                                disabled={isDisabled}
                                className="w-4 h-4"
                                style={{ accentColor: '#9E6752' }}
                              />
                              <Package className="w-12 h-12 p-2 rounded-lg" style={{ background: '#2D4354', color: '#FED7A5' }} />
                              <div className="flex-1">
                                <span className="text-sm font-medium block" style={{ color: '#20212B' }}>{deck.title}</span>
                                <span className="text-xs" style={{ color: '#73766A' }}>{deck.product_ids?.length || 0} products</span>
                              </div>
                            </label>
                          );
                        })}
                      {decks.filter(d => d.title.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                        <p className="text-sm p-4 rounded-lg text-center" style={{ background: '#FFF8F0', color: '#73766A' }}>
                          No decks match "{searchQuery}"
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: '#E5E5E5' }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFeaturedSection}
              disabled={!featuredFormData.name || !featuredFormData.category_name || selectedItems.length === 0}
              className="px-6"
              style={{ background: '#9E6752', color: 'white' }}
            >
              {editingFeatured ? '✓ Update Section' : '✓ Create Section'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
